import React from 'react';
import { NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom';
import b2bService from '../../services/b2bService';
import './B2bPortalLayout.css';
import './B2bPortalGlass.css';
import './B2bPortalPolish.css';

const B2bPortalLayout = () => {
  const navigate = useNavigate();

  // Retrieve client portal user details from sessionStorage (Zero-Trust Session Isolation)
  const portalUserData = (() => {
    try {
      return JSON.parse(sessionStorage.getItem('b2b_portal_user') || '{}');
    } catch {
      return {};
    }
  })();

  // the server allows invoices and the ledger only for admin and billing users
  const canSeeMoney = !/viewer/i.test(portalUserData.role || '');
  const canBook = !/viewer|billing/i.test(portalUserData.role || '') && portalUserData.allowPortalRegistration !== false;

  const handleLogout = async () => {
    try {
      await b2bService.portalLogout();
    } catch (err) {
      console.warn('Logout server notification failed:', err);
    } finally {
      sessionStorage.removeItem('b2b_portal_token');
      sessionStorage.removeItem('b2b_portal_user');
      navigate('/b2b/login');
    }
  };

  // no portal session in this tab (new tab, closed browser, expired): straight to the sign-in page
  if (!sessionStorage.getItem('b2b_portal_token')) return <Navigate to="/b2b/login" replace />;

  return (
    <div className="b2b-portal-shell">
      <header className="b2b-portal-header">
        <div className="b2b-header-inner">
          <div className="b2b-brand-section">
            <span className="b2b-brand-badge">B2B Portal</span>
            <div>
              <h1 className="b2b-portal-title">Client Access Hub</h1>
              <div className="b2b-party-name">
                {portalUserData.partyName || 'Diagnostic Partner'} ({portalUserData.partyCode || 'B2B'})
              </div>
            </div>
          </div>

          <nav className="b2b-nav-links">
            <NavLink
              to="/b2b/portal/dashboard"
              className={({ isActive }) => `b2b-nav-link ${isActive ? 'active' : ''}`}
            >
              Dashboard
            </NavLink>
            {canBook && (
              <NavLink
                to="/b2b/portal/book"
                className={({ isActive }) => `b2b-nav-link ${isActive ? 'active' : ''}`}
              >
                Book Order
              </NavLink>
            )}
            <NavLink
              to="/b2b/portal/orders"
              className={({ isActive }) => `b2b-nav-link ${isActive ? 'active' : ''}`}
            >
              Orders & Reports
            </NavLink>
            {canSeeMoney && <NavLink
              to="/b2b/portal/invoices"
              className={({ isActive }) => `b2b-nav-link ${isActive ? 'active' : ''}`}
            >
              Invoices
            </NavLink>}
            {canSeeMoney && <NavLink
              to="/b2b/portal/ledger"
              className={({ isActive }) => `b2b-nav-link ${isActive ? 'active' : ''}`}
            >
              Financial Ledger
            </NavLink>}
            {canSeeMoney && (
              <NavLink
                to="/b2b/portal/dashboard?action=pay"
                className="b2b-nav-link"
                style={{
                  background: 'rgba(16, 185, 129, 0.15)',
                  color: '#059669',
                  border: '1px solid rgba(16, 185, 129, 0.4)',
                  fontWeight: 700,
                  borderRadius: '6px',
                  padding: '5px 12px',
                  marginLeft: '4px'
                }}
              >
                💳 Make Payment
              </NavLink>
            )}
          </nav>

          <div className="b2b-user-section">
            <span className="b2b-user-avatar" aria-hidden="true">
              {(portalUserData.fullName || portalUserData.username || 'U').trim().charAt(0).toUpperCase()}
            </span>
            <div className="b2b-user-info">
              <div className="b2b-user-name">{portalUserData.fullName || portalUserData.username || 'Authorized User'}</div>
              <div className="b2b-user-role">{portalUserData.role || 'Client'}</div>
            </div>
            <button className="b2b-logout-btn" onClick={handleLogout} title="Sign out of client portal">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="b2b-portal-main">
        <Outlet />
      </main>

      <footer className="b2b-portal-footer">
        &copy; {new Date().getFullYear()} My LIS Enterprise — Secure B2B Laboratory Interoperability Portal.
      </footer>
    </div>
  );
};

export default B2bPortalLayout;
